import { isPhoneDeviceNow } from './isMobileDevice';
import { isPwaMode, isStandaloneDisplay } from './isPwaMode';

/**
 * Páginas públicas que se siguen abriendo en el navegador del celular: no son "la plataforma" sino enlaces de un solo propósito que
 * llegan por mail (aprobar/rechazar un certificado) o los usa el backoffice.
 */
const PUBLIC_PATHS = ['/cert-action', '/kb-preview'];

/**
 * ¿Hay que mostrar la landing "Instalá la app" en lugar de la plataforma web? Sí en el navegador de un CELULAR (no tablets).
 * No cuando:
 *  - ya corre como app instalada (`isPwaMode()`; la app se monta aparte);
 *  - es el popup del login con Google (`window.opener` en `/login`): tiene que montar la app completa para cerrar el circuito;
 *  - es una de las páginas públicas de `PUBLIC_PATHS` (ruta exacta: una subruta nueva NO queda exenta sin que alguien lo decida);
 *  - en desarrollo (`yarn start`) con `?web=1`, para poder probar la web con el celular emulado. En producción no hay salida.
 */
export function shouldShowInstallLanding(): boolean {
  if (typeof window === 'undefined') return false;
  if (isPwaMode() || isStandaloneDisplay()) return false;
  // El popup del login con Google vuelve a /login: solo ahí (cualquier otra página abierta con window.open sí ve la landing).
  if (window.opener && window.location.pathname === '/login') return false;
  if (!isPhoneDeviceNow()) return false;
  if (PUBLIC_PATHS.includes(window.location.pathname.replace(/\/+$/, '') || '/')) return false;
  if (process.env.NODE_ENV !== 'production' && new URLSearchParams(window.location.search).get('web') === '1') return false;
  return true;
}

export type LandingPlatform = 'ios' | 'android' | 'other';

/** Solo en desarrollo (`yarn start`): `?platform=ios|android` fuerza la plataforma para revisar cada guía sin tener el aparato. */
function devPlatformOverride(): LandingPlatform | null {
  if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') return null;
  const forced = new URLSearchParams(window.location.search).get('platform');
  return forced === 'ios' || forced === 'android' ? forced : null;
}

export function detectLandingPlatform(userAgent: string = navigator.userAgent): LandingPlatform {
  const forced = devPlatformOverride();
  if (forced) return forced;
  if (/iPhone|iPod|iPad/i.test(userAgent)) return 'ios';
  if (/Android/i.test(userAgent)) return 'android';
  return 'other';
}

/**
 * En iPhone solo se puede instalar desde Safari. Chrome, Firefox, Edge y los navegadores de adentro de otras apps (Instagram,
 * Facebook, WhatsApp…) no ofrecen "Agregar a inicio" de la misma forma: ahí hay que avisar que abra el link en Safari.
 */
export function isIosSafari(userAgent: string = navigator.userAgent): boolean {
  if (devPlatformOverride() === 'ios') return true;
  if (!/iPhone|iPod|iPad/i.test(userAgent)) return false;
  return !/CriOS|FxiOS|EdgiOS|OPiOS|GSA\/|FBAN|FBAV|Instagram|Line\/|Twitter|MicroMessenger|; wv\)/i.test(userAgent);
}

/**
 * `intent://` de Android para abrir la app instalada en el MISMO enlace que se tocó (ej. el de un mail o una notificación vieja), no
 * solo en la raíz. Solo usa `host`, `pathname` y `search` de la página (nunca valores armados a mano) y los escapa: en un intent, `;`
 * y `#` separan extras y no pueden venir de la URL. El esquema queda fijo en `https`; si ningún programa lo resuelve, vuelve a la misma
 * página (`browser_fallback_url`).
 */
export function buildAppIntentUrl(location: Pick<Location, 'host' | 'origin' | 'pathname' | 'search'>): string {
  const safe = (value: string) => encodeURI(value).replace(/;/g, '%3B').replace(/#/g, '%23');
  const path = `${safe(location.pathname || '/')}${safe(location.search)}`;
  return (
    `intent://${location.host}${path}#Intent;scheme=https;action=android.intent.action.VIEW;` +
    `S.browser_fallback_url=${encodeURIComponent(location.origin + path)};end`
  );
}
